import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  TIERS,
  getTierName as pricingGetTierName,
  getTierPrice as pricingGetTierPrice,
  getMaxTrainers as pricingGetMaxTrainers,
  getClientCap as pricingGetClientCap,
  isTeamTier as pricingIsTeamTier,
  tierHasCapability,
  normalizeTier,
  type SubscriptionTier,
  type TierCapability,
} from "@/lib/pricing";

export type { SubscriptionTier };
export type SubscriptionStatus = "active" | "trialing" | "past_due" | "canceled" | "unpaid" | "incomplete";

export interface Subscription {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  trial_start: string | null;
  trial_end: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  stripe_customer_id: string | null;
  is_inherited?: boolean;
  subscription_owner_id?: string;
}

interface ClientLimitCheck {
  allowed: boolean;
  reason?: string;
  limit?: number;
  current_count?: number;
  tier?: string;
}

interface TrainerLimitCheck {
  allowed: boolean;
  reason?: string;
  message?: string;
  limit?: number;
  current_count?: number;
  tier?: string;
}

export type AccountType = "individual" | "organization";

interface SubscriptionContextType {
  subscription: Subscription | null;
  accountType: AccountType;
  loading: boolean;
  isTrialing: boolean;
  trialDaysRemaining: number;
  isPastDue: boolean;
  isCanceled: boolean;
  isInherited: boolean;
  canAddClient: () => Promise<ClientLimitCheck>;
  canAddTrainer: () => Promise<TrainerLimitCheck>;
  refetch: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const DEFAULT_SUBSCRIPTION: Subscription = {
  tier: "starter",
  status: "trialing",
  trial_start: null,
  trial_end: null,
  current_period_start: null,
  current_period_end: null,
  cancel_at_period_end: false,
  stripe_customer_id: null,
};

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [accountType, setAccountType] = useState<AccountType>("individual");
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setAccountType("individual");
      setLoading(false);
      return;
    }

    try {
      // Fetch effective subscription (inherits from org owner) and account type in parallel
      const [subResult, profileResult] = await Promise.all([
        supabase.rpc("get_effective_subscription", { p_user_id: user.id }),
        supabase
          .from("profiles")
          .select("account_type")
          .eq("id", user.id)
          .single()
      ]);

      if (subResult.error) {
        console.error("Error fetching subscription:", subResult.error);
        setSubscription(DEFAULT_SUBSCRIPTION);
      } else if (subResult.data && subResult.data.length > 0) {
        // RPC returns array, take first result
        const sub = subResult.data[0];
        setSubscription({
          tier: normalizeTier(sub.tier),
          status: sub.status as SubscriptionStatus,
          trial_start: sub.trial_start,
          trial_end: sub.trial_end,
          current_period_start: sub.current_period_start,
          current_period_end: sub.current_period_end,
          cancel_at_period_end: sub.cancel_at_period_end,
          stripe_customer_id: sub.stripe_customer_id,
          is_inherited: sub.is_inherited,
          subscription_owner_id: sub.subscription_owner_id,
        });
      } else {
        setSubscription(DEFAULT_SUBSCRIPTION);
      }

      if (profileResult.data?.account_type) {
        setAccountType(profileResult.data.account_type as AccountType);
      }
    } catch (error) {
      console.error("Error in fetchSubscription:", error);
      setSubscription(DEFAULT_SUBSCRIPTION);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const isTrialing = subscription?.status === "trialing";
  const isPastDue = subscription?.status === "past_due";
  const isCanceled = subscription?.status === "canceled";
  const isInherited = subscription?.is_inherited ?? false;

  const trialDaysRemaining = (() => {
    if (!subscription?.trial_end || !isTrialing) return 0;
    const trialEnd = new Date(subscription.trial_end);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  })();

  const canAddClient = useCallback(async (): Promise<ClientLimitCheck> => {
    if (!user) {
      return { allowed: false, reason: "not_authenticated" };
    }

    try {
      const { data, error } = await supabase.rpc("can_add_client", {
        p_user_id: user.id,
      });

      if (error) {
        console.error("Error checking client limit:", error);
        // Fail CLOSED on paid-feature limits — do not allow bypass on RPC error.
        return { allowed: false, reason: "check_failed", message: "Could not verify your plan limits. Please try again." };
      }

      return data as ClientLimitCheck;
    } catch (error) {
      console.error("Error in canAddClient:", error);
      return { allowed: false, reason: "check_failed", message: "Could not verify your plan limits. Please try again." };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const canAddTrainer = useCallback(async (): Promise<TrainerLimitCheck> => {
    if (!user) {
      return { allowed: false, reason: "not_authenticated" };
    }

    try {
      // Get user's organization — check both profiles.organization_id (legacy,
      // populated on invited signup) AND organization_members (current-path,
      // populated on public signup owner flow).
      const { data: profile } = await supabase
        .from("profiles")
        .select("organization_id")
        .eq("id", user.id)
        .maybeSingle();

      let orgId = profile?.organization_id as string | null | undefined;

      if (!orgId) {
        const { data: membership } = await supabase
          .from("organization_members")
          .select("organization_id")
          .eq("user_id", user.id)
          .order("created_at", { ascending: true })
          .limit(1)
          .maybeSingle();
        orgId = membership?.organization_id;
      }

      if (!orgId) {
        return { allowed: false, reason: "no_organization", message: "No organization found" };
      }

      const { data, error } = await supabase.rpc("can_add_trainer", {
        p_org_id: orgId,
      });

      if (error) {
        console.error("Error checking trainer limit:", error);
        return { allowed: true };
      }

      return data as TrainerLimitCheck;
    } catch (error) {
      console.error("Error in canAddTrainer:", error);
      return { allowed: true };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        accountType,
        loading,
        isTrialing,
        trialDaysRemaining,
        isPastDue,
        isCanceled,
        isInherited,
        canAddClient,
        canAddTrainer,
        refetch: fetchSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
}

// Helpers — all delegate to src/lib/pricing.ts (single source of truth).
// Kept as re-exports for backwards compat with existing callers.

export function getTierDisplayName(tier: SubscriptionTier | string): string {
  return pricingGetTierName(normalizeTier(tier));
}

export function getTierPrice(tier: SubscriptionTier | string): number {
  return pricingGetTierPrice(normalizeTier(tier));
}

export function tierHasFeature(
  tier: SubscriptionTier | string,
  feature: string,
): boolean {
  return tierHasCapability(normalizeTier(tier), feature as TierCapability);
}

export function isTeamTier(tier: SubscriptionTier | string): boolean {
  return pricingIsTeamTier(normalizeTier(tier));
}

export function getMaxTrainers(tier: SubscriptionTier | string): number {
  return pricingGetMaxTrainers(normalizeTier(tier));
}
